import { useState, useEffect,useRef } from 'react';
import {
  Table,
  Button,
  Dialog,
  Form,
  Input,
  Textarea,
  Popconfirm,
  Tag,
  Space,
  MessagePlugin,
} from 'tdesign-react';
import { AddIcon, EditIcon, DeleteIcon, CheckIcon, CloseIcon } from 'tdesign-icons-react';
import { buyerApi } from '../../api';
import type { Buyer } from '../../types';
import CoverImageUploader from '../../components/CoverImageUploader';
const statusOptions = [
  { label: '待审核', value: 0, theme: 'warning' },
  { label: '已通过', value: 1, theme: 'success' },
  { label: '已拒绝', value: 2, theme: 'danger' },
];

export default function BuyerManage() {
  const [data, setData] = useState<Buyer[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [dialogVisible, setDialogVisible] = useState(false);
  const [auditDialogVisible, setAuditDialogVisible] = useState(false);
  const [editingBuyer, setEditingBuyer] = useState<Buyer | null>(null);
  const [auditingBuyer, setAuditingBuyer] = useState<Buyer | null>(null);
  const [auditRemark, setAuditRemark] = useState('');
  const [businessLicenseUrl, setBusinessLicenseUrl] = useState('');
  const formRef = useRef<any>(null);

  const columns = [
    { colKey: 'id', title: 'ID', width: 80 },
    { colKey: 'companyName', title: '公司名称' },
    { colKey: 'companyCode', title: '公司编码', width: 120 },
    { colKey: 'contactName', title: '联系人', width: 100 },
    { colKey: 'contactPhone', title: '联系电话', width: 140 },
    {
      colKey: 'status',
      title: '状态',
      width: 100,
      cell: ({ row }: any) => {
        const status = statusOptions.find(s => s.value === row.status);
        return <Tag theme={status?.theme as any}>{status?.label || '未知'}</Tag>;
      },
    },
    { colKey: 'createTime', title: '创建时间', width: 180 },
    {
      colKey: 'action',
      title: '操作',
      width: 420,
      cell: ({ row }: any) => (
        <Space>
          {row.status === 0 && (
            <>
              <Button
                theme="success"
                variant="text"
                icon={<CheckIcon />}
                onClick={() => handleAudit(row, 1)}
              >
                通过
              </Button>
              <Button
                theme="danger"
                variant="text"
                icon={<CloseIcon />}
                onClick={() => handleAudit(row, 2)}
              >
                拒绝
              </Button>
            </>
          )}
          <Button
            theme="primary"
            variant="text"
            icon={<EditIcon />}
            onClick={() => handleEdit(row)}
          >
            编辑
          </Button>
          <Popconfirm
            content="确定要删除该采购商吗？"
            onConfirm={() => handleDelete(row.id)}
          >
            <Button theme="danger" variant="text" icon={<DeleteIcon />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const fetchData = async (page = pagination.current, pageSize = pagination.pageSize) => {
    setLoading(true);
    try {
      const res = await buyerApi.getList({ pageNum: page, pageSize });
      if (res.code === 200) {
        setData(res.data.list);
        setPagination({
          current: res.data.pageNum,
          pageSize: res.data.pageSize,
          total: res.data.total,
        });
      }
    } catch (err) {
      MessagePlugin.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);
  const resetFormState = () => {
    setEditingBuyer(null);
    setBusinessLicenseUrl('')
    // 如果 formRef 有 reset 方法，也可以调用
    if (formRef.current) {
      formRef.current.reset();
    }
  };
  const handleAdd = () => {
    resetFormState();
    setDialogVisible(true);
  };

  const handleEdit = (buyer: Buyer) => {
    setEditingBuyer(buyer);
    setBusinessLicenseUrl(buyer.businessLicense || '');
    setDialogVisible(true);
  };

  const handleAudit = (buyer: Buyer, status: number) => {
    setAuditingBuyer(buyer);
    setAuditRemark('');
    setAuditDialogVisible(true);
  };

  const handleAuditSubmit = async (status: number) => {
    if (!auditingBuyer) return;
    
    try {
      const res = await buyerApi.audit({
        id: auditingBuyer.id,
        status,
        remark: auditRemark,
      });
      
      if (res.code === 200) {
        MessagePlugin.success(status === 1 ? '审核通过' : '审核拒绝');
        setAuditDialogVisible(false);
        fetchData();
      } else {
        MessagePlugin.error(res.message || '审核失败');
      }
    } catch (err) {
      MessagePlugin.error('审核失败');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await buyerApi.delete(id);
      if (res.code === 200) {
        MessagePlugin.success('删除成功');
        fetchData();
      } else {
        MessagePlugin.error(res.message || '删除失败');
      }
    } catch (err) {
      MessagePlugin.error('删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      // 【修复】通过 formRef 获取实例并验证
      const result = await formRef.current?.validate();
      
      // validate 返回 true 表示成功，或者包含错误信息
      if (result !== true) {
        return;
      }
      const values = formRef.current?.getFieldsValue(true) || {};
      const submitData = {
        ...values,
        businessLicense: businessLicenseUrl,
      };
      let res;
      if (editingBuyer) {
        res = await buyerApi.update(editingBuyer.id, submitData);
      } else {
        res = await buyerApi.create(submitData);
      }

      if (res.code === 200) {
        MessagePlugin.success(editingBuyer ? '更新成功' : '创建成功');
        setDialogVisible(false);
        fetchData();
      } else {
        MessagePlugin.error(res.message || '操作失败');
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">采购商档案</h1>
        <Button theme="primary" icon={<AddIcon />} onClick={handleAdd}>
          新增采购商
        </Button>
      </div>

      <Table
        data={data}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          onChange: (pageInfo: any) => fetchData(pageInfo.current, pageInfo.pageSize),
        }}
      />

      {/* 编辑对话框 */}
      <Dialog
        header={editingBuyer ? '编辑采购商' : '新增采购商'}
        visible={dialogVisible}
        onClose={() => setDialogVisible(false)}
        onConfirm={handleSubmit}
        width={600}
        key={editingBuyer ? `edit-${editingBuyer.id}` : 'add-new'}
      >
        <Form ref={formRef}
            initialData={editingBuyer || {}}  labelWidth={100}>
          <Form.FormItem label="公司名称" name="companyName" rules={[{ required: true, message: '请输入公司名称' }]}>
            <Input placeholder="请输入公司名称" />
          </Form.FormItem>
          <Form.FormItem label="公司编码" name="companyCode">
            <Input placeholder="请输入公司编码" />
          </Form.FormItem>
          <Form.FormItem label="联系人" name="contactName" rules={[{ required: true, message: '请输入联系人' }]}>
            <Input placeholder="请输入联系人" />
          </Form.FormItem>
          <Form.FormItem label="联系电话" name="contactPhone" rules={[{ required: true, message: '请输入联系电话' }]}>
            <Input placeholder="请输入联系电话" />
          </Form.FormItem>
          <Form.FormItem label="联系邮箱" name="contactEmail">
            <Input placeholder="请输入联系邮箱" />
          </Form.FormItem>
          <Form.FormItem label="营业执照" name="businessLicense">
            <CoverImageUploader 
                value={businessLicenseUrl} 
                onChange={(val) => {
                  setBusinessLicenseUrl(Array.isArray(val) ? val[0] : val);
                }} 
                simple
              />
          </Form.FormItem>
          <Form.FormItem label="经营范围" name="businessScope">
            <Textarea placeholder="请输入经营范围" rows={3} />
          </Form.FormItem>
          <Form.FormItem label="公司地址" name="address">
            <Input placeholder="请输入公司地址" />
          </Form.FormItem>
        </Form>
      </Dialog>

      {/* 审核对话框 */}
      <Dialog
        header="审核采购商"
        visible={auditDialogVisible}
        onClose={() => setAuditDialogVisible(false)}
        footer={
          <div className="flex justify-end gap-3">
            <Button onClick={() => setAuditDialogVisible(false)}>取消</Button>
            <Button theme="danger" onClick={() => handleAuditSubmit(2)}>拒绝</Button>
            <Button theme="success" onClick={() => handleAuditSubmit(1)}>通过</Button>
          </div>
        }
      >
        <div className="py-4">
          <p className="mb-4">采购商：{auditingBuyer?.companyName}</p>
          <div className="mb-2">审核备注：</div>
          <Textarea
            value={auditRemark}
            onChange={(value) => setAuditRemark(value)}
            placeholder="请输入审核备注（可选）"
            rows={3}
          />
        </div>
      </Dialog>
    </div>
  );
}
